import { ComponentType, lazy, ReactElement, Suspense } from "react";
import { useSelector } from "react-redux";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import HomePageSections from "../pages/home/components/main";

import AppErrorPage from "src/components/common/AppErrorPage";
import { selectUserToken } from "src/store/selectors/user";
import { lazyRetry } from "src/utils/lazyRetry";

const lazyPage = <T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
) => lazy(() => lazyRetry(importer));

const Page404 = lazyPage(() => import("src/pages/error/404"));
const AboutUsPage = lazyPage(() => import("src/pages/aboutUs"));
const BlogsPage = lazyPage(() => import("src/pages/blogs"));
const TermsConditionsPage = lazyPage(() => import("src/pages/terms"));
const RefundPolicyPage = lazyPage(() => import("src/pages/refundPolicy"));
const ShippingPolicyPage = lazyPage(() => import("src/pages/shippingPolicy"));
const PrivacyPolicyPage = lazyPage(() => import("src/pages/privacyPolicy"));
const HomePage = lazyPage(() => import("src/pages/home"));
const ForgotPassword = lazyPage(() => import("src/pages/forgotPassword"));
const ProductList = lazy(() =>
  lazyRetry(() => import("src/pages/productListingV2/ProductListingPage")),
);
const ResetPasswordForm = lazy(() =>
  lazyRetry(() => import("src/pages/registerV2/ResetPasswordForm")),
);
const Cart = lazyPage(() => import("src/pages/cart"));
const Checkout = lazyPage(() => import("src/pages/checkout"));
const Login = lazyPage(() => import("src/pages/login"));
const Register = lazyPage(() => import("src/pages/register"));
const OrderConfirmation = lazyPage(() => import("src/pages/orderConfirmation"));
const Profile = lazyPage(() => import("src/pages/profile"));
const CategoryProduct = lazyPage(() => import("src/pages/category/category"));
const PaymentCancellation = lazyPage(
  () => import("src/pages/paymentCancellation"),
);
const PaymentSuccessScreen = lazyPage(() => import("src/pages/paymentSuccess"));
const ProductOverview = lazyPage(() => import("src/pages/productDetail"));
const Blog = lazyPage(() => import("src/pages/blog"));
const OurTeamPage = lazyPage(() => import("src/pages/ourTeam"));
const ContactPage = lazyPage(() => import("src/pages/contact"));
const SupportPage = lazyPage(() => import("src/pages/support"));
const RewardsPage = lazyPage(() => import("src/pages/rewards"));
const FAQPage = lazyPage(() => import("src/pages/faq"));
const BlanksAppealsPage = lazyPage(() => import("src/pages/BlanksAppeals"));
const LoveecePage = lazyPage(() => import("src/pages/loveece"));
const LoveteePage = lazyPage(() => import("src/pages/lovetee"));
const ResetData = lazyPage(() => import("src/pages/resetData"));

const ProtectedRoute = ({ element }: { element: ReactElement }) => {
  const token = useSelector(selectUserToken);
  const location = useLocation();
  return token ? (
    element
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
};

const errorElement = <AppErrorPage />;

const router = createBrowserRouter([
  {
    path: "/",
    element: <HomePage />,
    errorElement,
    children: [
      {
        path: "/",
        element: <HomePageSections />,
      },
      {
        path: "/about-us",
        element: <AboutUsPage />,
      },
      {
        path: "/our-team",
        element: <OurTeamPage />,
      },
      {
        path: "/contact",
        element: <ContactPage />,
      },
      {
        path: "/support",
        element: <SupportPage />,
      },
      {
        path: "/wemust-club",
        element: <RewardsPage />,
      },
      {
        path: "/faq",
        element: <FAQPage />,
      },
      {
        path: "/blanks",
        element: <BlanksAppealsPage />,
      },
      {
        path: "/loveece",
        element: <LoveecePage />,
      },
      {
        path: "/lovetee",
        element: <LoveteePage />,
      },
      {
        path: "/blogs",
        element: <BlogsPage />,
      },
      {
        path: "/terms-and-conditions",
        element: <TermsConditionsPage />,
      },
      {
        path: "/refund-and-returns-policy",
        element: <RefundPolicyPage />,
      },
      {
        path: "/refund",
        element: <RefundPolicyPage />,
      },
      {
        path: "/privacy-policy",
        element: <PrivacyPolicyPage />,
      },
      {
        path: "/shipping-policy",
        element: <ShippingPolicyPage />,
      },
      {
        path: "/products",
        element: <ProductList />,
      },
      {
        path: "/products/:slug",
        element: <ProductOverview />,
      },
      {
        path: "/products-overview/:slug",
        element: <ProductOverview />,
      },
      {
        path: "/cart",
        element: <Cart />,
      },
      {
        path: "/checkout",
        element: <ProtectedRoute element={<Checkout />} />,
      },
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "/clear-data",
        element: <ResetData />,
      },
      {
        path: "/register",
        element: <Register />,
      },
      {
        path: "/orders",
        element: <OrderConfirmation />,
      },
      {
        path: "/profile",
        element: <ProtectedRoute element={<Profile />} />,
      },
      {
        path: "/blog/:slug",
        element: <Blog />,
      },
      {
        path: "/pages/:slug",
        element: <Blog />,
      },
      {
        path: "/category/:slug",
        element: <CategoryProduct />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "/reset-password/:email",
        element: <ResetPasswordForm />,
      },
      {
        path: "/error",
        element: <AppErrorPage />,
      },
      {
        path: "*",
        element: (
          <Suspense fallback={<>Loading...</>}>
            <Page404 />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: "/success",
    element: <PaymentSuccessScreen />,
    errorElement,
  },
  {
    path: "/cancel",
    element: <PaymentCancellation />,
    errorElement,
  },
]);

export default router;
